import React from 'react';
import { Loader2, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ButtonVariant = 
    | 'primary'      // Índigo sólido com sombra suave e glow
    | 'secondary'    // Borda slate sutil com hover discreto
    | 'outline'      // Borda índigo translúcida
    | 'ghost'        // Sem fundo, hover sutil
    | 'danger'       // Ações destrutivas / exclusão (rose/red)
    | 'magic'        // Gradiente IA violeta -> índigo com brilho
    | 'subtle'       // Fundo índigo super claro com texto índigo
    | 'dark';        // Slate escuro elegante

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /**
     * Variante visual do botão.
     * - `primary`: Botão de ação principal (índigo sólido)
     * - `magic`: Ações de IA, geração mágica ou destaques (gradiente violeta/índigo)
     * - `secondary`: Botão secundário de apoio (borda suave)
     * - `outline`: Borda índigo com fundo transparente
     * - `ghost`: Botão sem fundo para ações discretas
     * - `danger`: Ações destrutivas ou cancelamento (vermelho/rose)
     * - `subtle`: Fundo índigo bem leve
     * - `dark`: Fundo escuro premium
     */
    variant?: ButtonVariant;

    /**
     * Tamanho do botão:
     * - `xs`: Extra pequeno (tags/ações compactas)
     * - `sm`: Pequeno (barras de ferramentas / filtros)
     * - `md`: Médio (padrão da aplicação)
     * - `lg`: Grande (CTAs de destaque / landing / modais)
     */
    size?: ButtonSize;

    /** Ícone exibido à esquerda do texto */
    icon?: LucideIcon | React.ElementType;

    /** Ícone exibido à direita do texto */
    iconRight?: LucideIcon | React.ElementType;

    /** Exibe spinner de carregamento e desativa cliques automaticamente */
    loading?: boolean;

    /** Texto exibido durante o estado de carregamento (opcional) */
    loadingText?: string;

    /** Ocupa 100% da largura do elemento pai */
    fullWidth?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
    children,
    variant = 'primary',
    size = 'md',
    onClick,
    className = '',
    icon: Icon,
    iconRight: IconRight,
    disabled,
    loading = false,
    loadingText,
    fullWidth = false,
    type = 'button',
    ...props
}, ref) => {
    const baseStyle = "inline-flex items-center justify-center font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none select-none active:scale-[0.98]";

    const sizeStyles: Record<ButtonSize, { button: string; icon: number }> = {
        xs: { button: "px-2.5 py-1 text-xs rounded-lg gap-1.5", icon: 14 },
        sm: { button: "px-3.5 py-1.5 text-xs rounded-xl gap-1.5", icon: 16 },
        md: { button: "px-4 py-2 text-sm rounded-xl gap-2", icon: 18 },
        lg: { button: "px-5 py-2.5 text-base rounded-2xl gap-2.5 shadow-md", icon: 20 },
    };

    const variantStyles: Record<ButtonVariant, string> = {
        primary: "bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm shadow-indigo-600/25 hover:shadow-md hover:shadow-indigo-600/30 focus:ring-indigo-500 border border-indigo-600",
        magic: "bg-gradient-to-r from-violet-600 via-indigo-600 to-indigo-700 hover:from-violet-700 hover:via-indigo-700 hover:to-indigo-800 text-white shadow-sm shadow-indigo-600/30 hover:shadow-md hover:shadow-indigo-600/40 focus:ring-indigo-500 border border-indigo-500/30",
        secondary: "bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 border border-slate-200 shadow-sm focus:ring-slate-300",
        outline: "bg-transparent hover:bg-indigo-50/50 text-indigo-600 hover:text-indigo-700 border border-indigo-200 focus:ring-indigo-500",
        ghost: "bg-transparent hover:bg-slate-100 text-slate-600 hover:text-slate-900 border border-transparent focus:ring-slate-300",
        danger: "bg-rose-600 hover:bg-rose-700 text-white shadow-sm shadow-rose-600/25 hover:shadow-md hover:shadow-rose-600/30 focus:ring-rose-500 border border-rose-600",
        subtle: "bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-100 focus:ring-indigo-400",
        dark: "bg-slate-900 hover:bg-slate-800 text-white shadow-sm shadow-slate-900/20 focus:ring-slate-700 border border-slate-800",
    };

    const { button: sizeClass, icon: iconSize } = sizeStyles[size];

    return (
        <button
            ref={ref}
            type={type}
            onClick={onClick}
            className={cn(
                baseStyle,
                sizeClass,
                variantStyles[variant],
                variant === 'magic' && 'relative overflow-hidden group',
                fullWidth ? 'w-full' : '',
                className
            )}
            disabled={disabled || loading}
            {...props}
        >
            {/* Shimmer effect for magic variant */}
            {variant === 'magic' && !loading && !disabled && (
                <span className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none" />
            )}

            {loading ? (
                <Loader2 size={iconSize} className="animate-spin shrink-0" />
            ) : Icon ? (
                <Icon size={iconSize} className="shrink-0" />
            ) : null}

            {loading && loadingText ? (
                <span>{loadingText}</span>
            ) : (
                children
            )}

            {!loading && IconRight && (
                <IconRight size={iconSize} className="shrink-0" />
            )}
        </button>
    );
});

Button.displayName = 'Button';
